import { ReactElement, useState } from 'react'
import { Link } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { IconButton, styled, Tooltip } from '@mui/material'
import { red } from '@mui/material/colors'
import {
  DataGridPremium,
  GridColDef,
  GridRowsProp,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium'
import type { MyOrganisationDTO } from '@api/fastAPI.schemas'
import { useGetMyOrganisations } from '@api/my-organisation/my-organisation'
import { DeleteIconButton, RestoreFromTrashIconButton } from '@ui/actions'
import { AlertDialog } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import EditHistoryPopover from './EditHistoryPopover'
import PrimaryEditorCell from './PrimaryEditorCell'
import PrimaryEditorEditCell from './PrimaryEditorCell/PrimaryEditorEditCell'
import TagsCell from './TagsCell'

dayjs.extend(utc)

const StyledDataGridPremium = styled(DataGridPremium)({
  backgroundColor: 'white',
  '& .MuiDataGrid-main': {
    backgroundColor: 'white',
  },
  '& .trash-project-row': {
    backgroundColor: red[50],
    '&:hover': {
      backgroundColor: red[100],
    },
  },
})
interface Props {
  projects: ProjectData[]
  onMoveToTrash: (project: ProjectData) => void
  onRestoreFromTrash: (project: ProjectData) => void
  onDeletePermanently: (project: ProjectData) => void
  onEdit: (project: ProjectData) => void
  onDuplicate: (project: ProjectData) => void
  onReassign: (project: ProjectData, primaryEditorGuid: string | null) => void
}

const ProjectTable = ({
  projects,
  onMoveToTrash,
  onRestoreFromTrash,
  onDeletePermanently,
  onEdit,
  onDuplicate,
  onReassign,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const [editHistoryTarget, setEditHistoryTarget] = useState<{
    anchorEl: HTMLElement
    projectGuid: string
  } | null>(null)

  // R11/R12: binning, deleting and reassigning a project are the *project's
  // own organisation's* admins' to do -- and the list spans every
  // organisation the caller belongs to, so this has to be resolved per row,
  // not from whichever organisation happens to be active. The backend
  // refuses the rest with 403 organisation_admin_required; this only keeps
  // the buttons honest.
  const { data: organisations } = useGetMyOrganisations<MyOrganisationDTO[], AxiosError>({
    query: {
      select: data => data.organisations,
      retry: false,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Organisationen'), {
          variant: 'error',
        })
      },
    },
  })
  const adminOrganisationGuids = new Set(
    (organisations || []).filter(organisation => organisation.is_admin).map(({ guid }) => guid),
  )

  const rows: GridRowsProp = projects.map(project => ({
    id: project.guid,
    name: project.name,
    created: project.created_timestamp,
    edited: project.last_edited_timestamp,
    primaryEditor: project.primary_editor_name,
    // A string so the quick filter matches tags; the cell renders the chips
    // from the project itself.
    tags: (project.tags ?? []).join(', '),
    project,
  }))

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Projekt Name',
      // the longest value in the table, and what the row is scanned for
      flex: 2,
      renderCell: params => {
        if (params.row.project.status === 'trash') {
          return params.value
        }

        return (
          <Link
            to={`/editor/${params.row.id}/`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {params.value}
          </Link>
        )
      },
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      renderCell: params => <TagsCell tags={params.row.project.tags ?? []} />,
    },
    {
      field: 'created',
      headerName: 'Erstellt am',
      flex: 1,
      valueFormatter: value => dayjs.utc(value).local().format('DD.MM.YYYY HH:mm:ss'),
    },
    {
      field: 'edited',
      headerName: 'Editiert am',
      // a touch wider than a bare date: it also carries the history button
      flex: 1,
      minWidth: 170,
      valueFormatter: value => dayjs.utc(value).local().format('DD.MM.YYYY HH:mm'),
      renderCell: params => {
        return (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            width="100%"
            height="100%"
            onClick={event =>
              setEditHistoryTarget({ anchorEl: event.currentTarget, projectGuid: params.row.id })
            }
            sx={{ cursor: 'pointer', minWidth: 0 }}
            data-cy="project-edited-cell"
          >
            {params.formattedValue}
            <Tooltip title="Bearbeitungsverlauf" enterDelay={1000}>
              <IconButton
                size="small"
                onClick={event => {
                  // the cell itself opens the popover too -- anchor it to the
                  // button rather than letting both handlers fight over it
                  event.stopPropagation()
                  setEditHistoryTarget({
                    anchorEl: event.currentTarget,
                    projectGuid: params.row.id,
                  })
                }}
                data-cy="project-edit-history-button"
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'primaryEditor',
      headerName: 'Bearbeiter',
      flex: 1,
      // The Primärbearbeiter: display and filtering only, never access. Absent
      // for a project nobody is assigned to, and nameless for a subject whose
      // profile was never synced -- an auth0|… id helps no one, so the cell
      // itself falls back to a dash (the single place that decides that).
      valueGetter: (value, row) => row.project.primary_editor_name,
      renderCell: params => <PrimaryEditorCell {...params} />,
      editable: true,
      renderEditCell: params => <PrimaryEditorEditCell {...params} onReassign={onReassign} />,
      // without this the custom cell sits at the top of the row instead of
      // being centred like the plain value columns
      display: 'flex',
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      flex: 1,
      renderCell: params => {
        const project = params.row.project
        const isInTrash = project.status === 'trash'
        const isOrganisationAdmin = adminOrganisationGuids.has(project.organization_guid)

        return (
          <Box display="flex" justifyContent="flex-end">
            {!isInTrash && (
              <>
                {/* the project name links to the editor too, but people miss it */}
                <Tooltip title="Projekt öffnen" enterDelay={1000}>
                  <IconButton
                    component={Link}
                    to={`/editor/${project.guid}/`}
                    data-cy="btn-open-project"
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bearbeiten" enterDelay={1000}>
                  <IconButton onClick={async () => onEdit(project)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplizieren" enterDelay={1000}>
                  <IconButton onClick={async () => onDuplicate(project)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isOrganisationAdmin && (
                  <AlertDialog
                    title="In Papierkorb verschieben?"
                    text={`Bestätigen Sie, dass Sie das Projekt ${project.name} in den Papierkorb verschieben wollen.`}
                    confirmLabel="In Papierkorb verschieben"
                    onConfirm={async () => onMoveToTrash(project)}
                  >
                    <DeleteIconButton tooltip="In Papierkorb verschieben" enterDelay={1000} />
                  </AlertDialog>
                )}
              </>
            )}
            {isInTrash && (
              <>
                <RestoreFromTrashIconButton
                  tooltip="Wiederherstellen"
                  enterDelay={1000}
                  onClick={async () => onRestoreFromTrash(project)}
                />
                {isOrganisationAdmin && (
                  <AlertDialog
                    title="Projekt endgültig löschen?"
                    text={`Das Projekt ${project.name} befindet sich im Papierkorb. Bestätigen Sie, dass Sie es endgültig und unwiderruflich entfernen wollen.`}
                    confirmLabel="Endgültig löschen"
                    onConfirm={async () => onDeletePermanently(project)}
                  >
                    <DeleteIconButton tooltip="Endgültig löschen" enterDelay={1000} />
                  </AlertDialog>
                )}
              </>
            )}
          </Box>
        )
      },
      align: 'left',
      display: 'flex',
    },
  ]

  return (
    <Box>
      <StyledDataGridPremium
        rows={rows}
        columns={columns}
        getRowClassName={params =>
          params.row.project.status === 'trash' ? 'trash-project-row' : ''
        }
        isCellEditable={params =>
          params.field === 'primaryEditor' &&
          adminOrganisationGuids.has(params.row.project.organization_guid)
        }
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        slots={{
          toolbar: () => (
            <Box sx={{ p: 2 }}>
              <GridToolbarQuickFilter sx={{ width: '100%' }} />
            </Box>
          ),
        }}
        density="compact"
        initialState={{
          sorting: {
            // show latest projects first
            sortModel: [{ field: 'created', sort: 'desc' }],
          },
          pagination: {
            paginationModel: { pageSize: 50 },
          },
        }}
      />
      <EditHistoryPopover
        anchorEl={editHistoryTarget?.anchorEl ?? null}
        projectGuid={editHistoryTarget?.projectGuid ?? null}
        onClose={() => setEditHistoryTarget(null)}
      />
    </Box>
  )
}

export default ProjectTable
